/**
 * Variation Orders API Service
 * Phase 1: Core VO Foundation
 * Phase 2: Commercial & Pricing (RFQ, Quotations, QS Assessment)
 */

import { api } from './index';

export const variationOrdersApi = {
  // CRUD Operations
  create: (data) => api.post('/variation-orders', data),
  
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/variation-orders?${queryParams.toString()}`);
  },
  
  getById: (id) => api.get(`/variation-orders/${id}`),
  
  update: (id, data) => api.put(`/variation-orders/${id}`, data),
  
  delete: (id) => api.delete(`/variation-orders/${id}`),
  
  // Stats
  getStats: (projectId) => {
    const params = projectId ? `?project_id=${projectId}` : '';
    return api.get(`/variation-orders/stats${params}`);
  },
  
  // Dashboard Analytics (detailed)
  getDashboardAnalytics: (projectId) => {
    const params = projectId ? `?project_id=${projectId}` : '';
    return api.get(`/variation-orders/dashboard/analytics${params}`);
  },
  
  // Line Items
  addLineItem: (voId, item) => api.post(`/variation-orders/${voId}/line-items`, item),
  
  updateLineItem: (voId, itemId, item) => 
    api.put(`/variation-orders/${voId}/line-items/${itemId}`, item),
  
  deleteLineItem: (voId, itemId) => 
    api.delete(`/variation-orders/${voId}/line-items/${itemId}`),
  
  // Approvals
  addApproval: (voId, approvalType, status, notes, conditions) => 
    api.post(`/variation-orders/${voId}/approvals`, null, {
      params: { approval_type: approvalType, status, notes, conditions }
    }),
  
  // Workflow Actions
  issueRfq: (voId, issuedTo, notes) => 
    api.post(`/variation-orders/${voId}/issue-rfq`, null, {
      params: { issued_to: issuedTo, notes }
    }),
  
  submitForReview: (voId) => 
    api.post(`/variation-orders/${voId}/submit-for-review`),
  
  recommend: (voId, assessedTotal, notes) => 
    api.post(`/variation-orders/${voId}/recommend`, null, {
      params: { assessed_total: assessedTotal, notes }
    }),
  
  startWork: (voId) => 
    api.post(`/variation-orders/${voId}/start-work`),
  
  markImplemented: (voId, notes) => 
    api.post(`/variation-orders/${voId}/mark-implemented`, null, {
      params: { notes }
    }),
  
  closeVo: (voId, finalAmount, notes) => 
    api.post(`/variation-orders/${voId}/close`, null, {
      params: { final_amount: finalAmount, notes }
    }),
  
  // Enums
  getEnums: () => api.get('/variation-orders/enums/all'),
  
  // ============== Phase 2: RFQ Management ==============
  
  // RFQs
  createRfq: (voId, data) => api.post(`/variation-orders/${voId}/rfqs`, data),
  
  listRfqs: (voId) => api.get(`/variation-orders/${voId}/rfqs`),
  
  getRfq: (voId, rfqId) => api.get(`/variation-orders/${voId}/rfqs/${rfqId}`),
  
  closeRfq: (voId, rfqId) => api.patch(`/variation-orders/${voId}/rfqs/${rfqId}/close`),
  
  // ============== Phase 2: Quotation Management ==============
  
  // Quotations
  createQuotation: (voId, data) => api.post(`/variation-orders/${voId}/quotations`, data),
  
  listQuotations: (voId) => api.get(`/variation-orders/${voId}/quotations`),
  
  getQuotation: (voId, quotationId) => 
    api.get(`/variation-orders/${voId}/quotations/${quotationId}`),
  
  updateQuotation: (voId, quotationId, data) => 
    api.put(`/variation-orders/${voId}/quotations/${quotationId}`, data),
  
  acceptQuotation: (voId, quotationId, notes) => 
    api.post(`/variation-orders/${voId}/quotations/${quotationId}/accept`, null, {
      params: { notes }
    }),
  
  rejectQuotation: (voId, quotationId, reason) => 
    api.post(`/variation-orders/${voId}/quotations/${quotationId}/reject`, null, {
      params: { reason }
    }),
  
  // ============== Phase 2: QS Assessment ==============
  
  // Assessments
  createAssessment: (voId, quotationId, data) => 
    api.post(`/variation-orders/${voId}/quotations/${quotationId}/assessment`, data),
  
  listQuotationAssessments: (voId, quotationId) => 
    api.get(`/variation-orders/${voId}/quotations/${quotationId}/assessments`),
  
  listVOAssessments: (voId) => api.get(`/variation-orders/${voId}/assessments`),
  
  // Commercial Summary
  getCommercialSummary: (voId) => api.get(`/variation-orders/${voId}/commercial-summary`),
  
  // ============== Phase 3: Module Integrations ==============
  
  // Linked Items
  addLinkedItem: (voId, data) => api.post(`/variation-orders/${voId}/linked-items`, data),
  
  removeLinkedItem: (voId, itemId) => 
    api.delete(`/variation-orders/${voId}/linked-items/${itemId}`),
  
  getLinkedItems: (voId, itemType) => {
    const params = itemType ? `?item_type=${itemType}` : '';
    return api.get(`/variation-orders/${voId}/linked-items${params}`);
  },
  
  getLinkableItems: (voId, itemType, search) => {
    const params = new URLSearchParams({ item_type: itemType });
    if (search) params.append('search', search);
    return api.get(`/variation-orders/${voId}/linkable-items?${params.toString()}`);
  },
  
  // Time Impact
  updateTimeImpact: (voId, data) => api.put(`/variation-orders/${voId}/time-impact`, null, { params: data }),
  
  getTimeImpactDetails: (voId) => api.get(`/variation-orders/${voId}/time-impact`),
  
  // Create VO from RFI
  createFromRfi: (rfiId, title, changeCategory) => 
    api.post(`/variation-orders/from-rfi/${rfiId}`, null, { 
      params: { title, change_category: changeCategory } 
    }),
  
  // ============== Phase 5: Payments & Deposits ==============
  
  // Payment Plan
  createPaymentPlan: (voId, data) => api.post(`/variation-orders/${voId}/payment-plan`, data),
  
  getPaymentPlan: (voId) => api.get(`/variation-orders/${voId}/payment-plan`),
  
  updatePaymentPlan: (voId, data) => api.put(`/variation-orders/${voId}/payment-plan`, data),
  
  // Securities
  addSecurity: (voId, data) => api.post(`/variation-orders/${voId}/payment-plan/securities`, data),
  
  removeSecurity: (voId, securityId) => 
    api.delete(`/variation-orders/${voId}/payment-plan/securities/${securityId}`),
  
  releaseSecurity: (voId, securityId, notes) => 
    api.patch(`/variation-orders/${voId}/payment-plan/securities/${securityId}/release`, null, {
      params: { notes }
    }),
  
  // Repayments
  addRepayment: (voId, data) => api.post(`/variation-orders/${voId}/payment-plan/repayments`, null, {
    params: data
  }),
  
  markRepaymentDeducted: (voId, repaymentId, actualAmount, certificateNumber) => 
    api.patch(`/variation-orders/${voId}/payment-plan/repayments/${repaymentId}/deduct`, null, {
      params: { actual_amount: actualAmount, certificate_number: certificateNumber }
    }),
  
  generateRepaymentSchedule: (voId, numCertificates) => 
    api.post(`/variation-orders/${voId}/payment-plan/generate-repayment-schedule`, null, {
      params: { num_certificates: numCertificates }
    }),
  
  // Certificate Linkage
  linkCertificate: (voId, data) => api.post(`/variation-orders/${voId}/payment-plan/link-certificate`, null, {
    params: data
  }),
  
  getLinkedCertificates: (voId) => api.get(`/variation-orders/${voId}/payment-plan/certificates`),
  
  markPaymentReceived: (voId, data) => api.patch(`/variation-orders/${voId}/payment-plan/mark-paid`, null, {
    params: data
  }),
  
  // Payment Summary
  getPaymentSummary: (voId) => api.get(`/variation-orders/${voId}/payment-summary`),
  
  // Phase 6: Approval Matrix
  getApprovalMatrix: (projectId) => {
    const params = projectId ? `?project_id=${projectId}` : '';
    return api.get(`/variation-orders/settings/approval-matrix${params}`);
  },
  
  createApprovalMatrixRule: (data) => api.post('/variation-orders/settings/approval-matrix', data),
  
  updateApprovalMatrixRule: (ruleId, data) => api.put(`/variation-orders/settings/approval-matrix/${ruleId}`, data),
  
  deleteApprovalMatrixRule: (ruleId) => api.delete(`/variation-orders/settings/approval-matrix/${ruleId}`),
  
  getApprovalRequirements: (voId) => api.get(`/variation-orders/${voId}/approval-requirements`),
  
  // Phase 6: Compliance Timers
  getComplianceSettings: (projectId) => api.get(`/variation-orders/settings/compliance?project_id=${projectId}`),
  
  saveComplianceSettings: (data) => api.post('/variation-orders/settings/compliance', data),
  
  getComplianceTimers: (voId) => api.get(`/variation-orders/${voId}/compliance-timers`),
  
  createComplianceTimer: (voId, data) => api.post(`/variation-orders/${voId}/compliance-timers`, data),
  
  completeComplianceTimer: (voId, timerId, notes) => 
    api.patch(`/variation-orders/${voId}/compliance-timers/${timerId}/complete`, null, {
      params: { notes }
    }),
  
  escalateComplianceTimer: (voId, timerId, escalateTo, notes) => 
    api.patch(`/variation-orders/${voId}/compliance-timers/${timerId}/escalate`, null, {
      params: { escalate_to: escalateTo, notes }
    }),
  
  getComplianceDashboard: (projectId) => {
    const params = projectId ? `?project_id=${projectId}` : '';
    return api.get(`/variation-orders/compliance/dashboard${params}`);
  },
};

export default variationOrdersApi;
